import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Megaphone } from "lucide-react";

const SOURCES = [
  { key: "all", label: "Tümü", endpoint: "/announcements" },
  { key: "gib", label: "GİB", endpoint: "/announcements/gib" },
  { key: "turmob", label: "TÜRMOB", endpoint: "/announcements/turmob" },
  { key: "resmi_gazete", label: "Resmi Gazete", endpoint: "/announcements/resmi-gazete" },
];

const DATE_RANGES = [
  { key: "all", label: "Tüm Zamanlar" },
  { key: "today", label: "Bugün" },
  { key: "7", label: "Son 7 Gün" },
  { key: "30", label: "Son 30 Gün" },
];

function parseDate(str) {
  if (!str) return null;
  const parts = str.split(".");
  if (parts.length !== 3) return null;
  const d = parts[0];
  const m = parts[1];
  const y = parts[2];
  return new Date(y + "-" + m + "-" + d + "T00:00:00");
}

function sourceBadgeClass(source) {
  if (source === "GİB")
    return "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400";
  if (source === "TÜRMOB")
    return "bg-slate-100 text-slate-700 dark:bg-slate-500/10 dark:text-slate-300";
  return "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400";
}

export default function MevzuatTakip() {
  const [activeSource, setActiveSource] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback((sourceKey) => {
    setLoading(true);
    setError(false);
    const src = SOURCES.find((s) => s.key === sourceKey) || SOURCES[0];
    api
      .get(src.endpoint)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(activeSource);
  }, [activeSource, load]);

  const now = new Date();
  const filtered = items.filter((it) => {
    if (dateRange === "all") return true;
    const d = parseDate(it.published_at);
    if (!d) return true;
    const diffDays = (now - d) / (1000 * 60 * 60 * 24);
    if (dateRange === "today") return diffDays < 1;
    if (dateRange === "7") return diffDays <= 7;
    if (dateRange === "30") return diffDays <= 30;
    return true;
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Megaphone size={22} /> Mevzuat Takip
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Resmi Gazete, GİB ve TÜRMOB kaynaklı güncel duyuru ve mevzuat değişiklikleri
        </p>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <Tabs value={activeSource} onValueChange={setActiveSource}>
            <TabsList>
              {SOURCES.map((s) => (
                <TabsTrigger key={s.key} value={s.key}>
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-full md:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGES.map((r) => (
                <SelectItem key={r.key} value={r.key}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-5">
        {loading ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Yükleniyor...</p>
        ) : error ? (
          <p className="text-sm text-muted-foreground py-10 text-center">
            Duyurular yüklenirken bir hata oluştu.
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">
            Bu filtrelere uygun duyuru bulunamadı.
          </p>
        ) : (
          <div className="space-y-1">
            {filtered.map((item, i) => {
              const key = item.source + "-" + item.url + "-" + i;
              const badgeClass =
                "shrink-0 text-[10px] font-semibold px-2 py-1 rounded mt-0.5 " +
                sourceBadgeClass(item.source);
              return (
                <a
                  key={key}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 px-3 py-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <span className={badgeClass}>{item.source}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{item.title}</div>
                    {item.category && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {item.category}
                      </div>
                    )}
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                    {item.published_at || ""}
                  </span>
                </a>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
